import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!article) {
    return (
      <div className="flex flex-col min-h-screen">
        <section className="pt-32 pb-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/articles" className="text-primary-600 hover:text-primary-700 font-label font-medium text-sm">
              返回文章列表
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const paragraphs = article.content.split('\n\n').filter(Boolean);

  return (
    <div className="flex flex-col min-h-screen">
      <main>
        <article>
          <section className="pt-24 pb-8 md:pt-32 md:pb-12 bg-background-100">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
              <div className="mb-6">
                <Breadcrumb items={[
                  { label: '首页', path: '/' },
                  { label: '行业洞察', path: '/articles' },
                  { label: article.title },
                ]} />
              </div>

              <div className="mb-4">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-medium">
                  {article.category}
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 font-label">
                <span>
                  <i className="ri-calendar-line mr-1"></i>
                  {article.date}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>
                  <i className="ri-time-line mr-1"></i>
                  {article.readTime}阅读
                </span>
              </div>
            </div>
          </section>

          <section className="py-8 bg-background-50">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
              <div className="rounded-lg overflow-hidden mb-10">
                <img
                  src={article.image}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="prose-custom max-w-none">
                {paragraphs.map((para, index) => (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5 font-body">
                    {para.trim()}
                  </p>
                ))}
              </div>

              <div className="mt-10 pt-8 border-t border-background-200/60">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground-600 font-label font-medium">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-background-100 text-xs font-label text-foreground-600"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-5 rounded-lg bg-background-100 border border-background-200/60">
                <div>
                  <p className="text-sm font-label font-semibold text-foreground-950 mb-1">
                    这篇文章对您有帮助吗？
                  </p>
                  <p className="text-xs text-foreground-500 font-body">
                    分享给您的团队，一起了解btbxx最新行业洞察
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button className="px-4 py-2 rounded-md bg-background-50 border border-background-300 text-foreground-700 font-label text-sm hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap">
                    <i className="ri-thumb-up-line mr-1"></i>
                    有帮助
                  </button>
                  <button className="px-4 py-2 rounded-md bg-primary-500 text-background-50 font-label text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap">
                    <i className="ri-share-forward-line mr-1"></i>
                    分享文章
                  </button>
                </div>
              </div>
            </div>
          </section>

          {relatedArticles.length > 0 && (
            <section className="py-12 md:py-16 bg-background-100">
              <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 block"
                    >
                      <div className="h-40 overflow-hidden">
                        <img
                          src={related.image}
                          alt={related.title}
                          title={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 mb-2">
                          {related.title}
                        </h3>
                        <div className="flex items-center gap-3 text-xs text-foreground-500 font-label">
                          <span>{related.date}</span>
                          <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                          <span>{related.readTime}阅读</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>
      </main>

      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        datePublished: article.date,
        author: { '@type': 'Organization', name: 'btbxx' },
        publisher: { '@type': 'Organization', name: 'btbxx', url: 'https://www.btbxx.com/' },
        image: article.image,
        url: `https://www.btbxx.com/article/${article.id}`,
        mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.btbxx.com/article/${article.id}` },
        keywords: article.tags.join(', '),
      })}</script>
    </div>
  );
}